import { useQuery } from '@tanstack/react-query';
import { BaseHTTPError } from '@fe-core/errors';
import { getFilters } from '@api/queries/filters';
import { TCategory, TCategoryIo } from '@api/io/categories.io';
import { TFilter } from '@api/io/filters.io';
import { useConfigContext } from '@fe-core';

const QUERY_KEY = 'filter-categories';

type ReturnType = {
  categories: TCategory[];
  filters: TFilter[];
};

const addParentIdRecursively = (
  categories: TCategoryIo[],
  parentId?: number,
) => {
  if (!categories) return [];

  return categories.map((cat) => {
    const newCat = { ...cat, parentId: parentId || null };
    if (cat.children) {
      newCat.children = addParentIdRecursively(cat.children, cat.id);
    }
    return newCat;
  });
};

const queryFn = async ({
  baseUrl,
}: {
  baseUrl: string;
}): Promise<ReturnType> => {
  const response = await getFilters({ baseUrl });

  return {
    categories: addParentIdRecursively(response.categories),
    filters: response.filters,
  };
};

export const useFilters = ({ enabled }: { enabled: boolean }) => {
  const { config } = useConfigContext();
  if (!config.apiUrls.ccapi) {
    throw new Error('Missing API URL');
  }

  return useQuery<ReturnType, BaseHTTPError>({
    queryKey: [QUERY_KEY],
    queryFn: async () => queryFn({ baseUrl: config.apiUrls.ccapi as string }),
    retry: false,
    staleTime: Infinity,
    enabled: config.loggedIn && enabled,
  });
};
